import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';
import { createConnectAccount } from '../../../services/stripeConnectService';
import { certName } from '../../../utils/certifications';
import toast from 'react-hot-toast';

const ExpertApprovalQueue = () => {
  const [pendingExperts, setPendingExperts] = useState([]);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commissionOverride, setCommissionOverride] = useState('');

  useEffect(() => {
    loadPendingExperts();
    provisionMissingConnectAccounts();
  }, []);

  const loadPendingExperts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('expert_profiles')
        ?.select('*')
        ?.eq('status', 'pending')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      setPendingExperts(data || []);
    } catch (error) {
      console.error('Load pending experts error:', error);
      toast?.error('Failed to load pending experts');
    } finally {
      setLoading(false);
    }
  };

  // Background: silently provision Connect accounts for any approved expert
  // who doesn't have one yet (handles experts approved before this feature).
  const provisionMissingConnectAccounts = async () => {
    try {
      const { data: experts } = await supabase
        ?.from('expert_profiles')
        ?.select('id, full_name')
        ?.eq('status', 'approved')
        ?.is('stripe_connect_account_id', null);

      if (!experts?.length) return;

      for (const expert of experts) {
        // Email is resolved server-side by the edge function
        createConnectAccount(expert.id, null, expert.full_name)
          .then((res) => {
            if (res?.success) {
              console.log(`Auto-provisioned Connect account for expert ${expert.id}: ${res.accountId}`);
            } else {
              console.warn(`Failed to auto-provision Connect for expert ${expert.id}:`, res?.error);
            }
          })
          .catch(console.warn);
      }
    } catch (e) {
      console.warn('provisionMissingConnectAccounts error:', e);
    }
  };

  const handleApprove = async (expertId) => {
    try {
      const updateData = {
        status: 'approved',
        verified: true,
        approved_at: new Date()?.toISOString()
      };

      // Add commission override if set
      if (commissionOverride && selectedExpert?.id === expertId) {
        const pct = parseFloat(commissionOverride);
        if (!isNaN(pct) && pct >= 0 && pct <= 100) {
          updateData.commission_pct = pct;
        }
      }

      const { error } = await supabase
        ?.from('expert_profiles')
        ?.update(updateData)
        ?.eq('id', expertId);

      if (error) throw error;

      // Auto-provision Stripe Connect account so the expert can receive
      // payments immediately without needing to visit their dashboard first.
      const expert = pendingExperts.find((e) => e.id === expertId) || selectedExpert;
      // Email resolved server-side by the edge function
      createConnectAccount(expert.id, null, expert.full_name)
          .then((res) => {
            if (!res?.success && !res?.alreadyExists) {
              console.warn('Connect account provisioning failed after approval:', res?.error);
            }
          })
          .catch(console.warn);

      toast?.success('Expert approved & Stripe account provisioned');
      setSelectedExpert(null);
      setCommissionOverride('');
      loadPendingExperts();
    } catch (error) {
      console.error('Approve expert error:', error);
      toast?.error('Failed to approve expert');
    }
  };

  const handleReject = async (expertId) => {
    try {
      const { error } = await supabase
        ?.from('expert_profiles')
        ?.update({ status: 'rejected' })
        ?.eq('id', expertId);

      if (error) throw error;

      toast?.success('Expert rejected');
      setSelectedExpert(null);
      loadPendingExperts();
    } catch (error) {
      console.error('Reject expert error:', error);
      toast?.error('Failed to reject expert');
    }
  };

  const handleViewDetails = (expert) => {
    setSelectedExpert(expert);
    setCommissionOverride(expert?.commission_pct?.toString() || '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader" className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {pendingExperts?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="CheckCircle" className="w-16 h-16 text-success mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Pending Applications</h3>
          <p className="text-muted-foreground">All expert applications have been reviewed.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {!selectedExpert ? (
            pendingExperts?.map((expert) => (
              <div
                key={expert?.id}
                className="bg-card border border-border rounded-lg p-4 md:p-6 hover:shadow-elevation-md transition-smooth"
              >
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-shrink-0">
                    <Image
                      src={expert?.avatar_url || 'https://via.placeholder.com/80'}
                      alt={`${expert?.full_name} avatar`}
                      className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-2 mb-3">
                      <div>
                        <h4 className="font-semibold text-lg text-foreground">{expert?.full_name}</h4>
                        <p className="text-sm text-muted-foreground">{expert?.title}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="data-text text-primary font-medium">
                          ${expert?.hourly_rate?.toFixed(2)}/hr
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {expert?.certifications?.map((cert, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-primary/10 text-primary text-xs rounded"
                          >
                            {certName(cert)}
                          </span>
                      ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3 text-sm">
                      <div>
                        <p className="text-muted-foreground">Experience</p>
                        <p className="text-foreground">{expert?.years_experience} years</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Submitted</p>
                        <p className="text-foreground">
                          {new Date(expert?.created_at)?.toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="mb-3">
                      <p className="text-muted-foreground text-sm mb-1">Specialties</p>
                      <p className="text-foreground text-sm">{expert?.specialties?.join(', ')}</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Eye"
                        onClick={() => handleViewDetails(expert)}
                      >
                        Review Application
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        iconName="CheckCircle"
                        onClick={() => handleApprove(expert?.id)}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="error"
                        size="sm"
                        iconName="XCircle"
                        onClick={() => handleReject(expert?.id)}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-card border border-border rounded-lg p-6">
              <Button
                variant="outline"
                size="sm"
                iconName="ArrowLeft"
                onClick={() => setSelectedExpert(null)}
                className="mb-4"
              >
                Back to List
              </Button>

              <div className="flex gap-6 mb-6">
                <Image
                  src={selectedExpert?.avatar_url || 'https://via.placeholder.com/120'}
                  alt={`${selectedExpert?.full_name} avatar`}
                  className="w-24 h-24 rounded-full object-cover"
                />
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-foreground mb-1">
                    {selectedExpert?.full_name}
                  </h3>
                  <p className="text-muted-foreground mb-3">{selectedExpert?.title}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedExpert?.certifications?.map((cert, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-primary/10 text-primary text-sm rounded"
                        >
                          {certName(cert)}
                        </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Hourly Rate</p>
                  <p className="text-lg font-semibold text-foreground">
                    ${selectedExpert?.hourly_rate?.toFixed(2)}/hr
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Years of Experience</p>
                  <p className="text-lg font-semibold text-foreground">
                    {selectedExpert?.years_experience} years
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-foreground mb-2">Specialties</h4>
                <p className="text-muted-foreground">{selectedExpert?.specialties?.join(', ')}</p>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-foreground mb-2">Bio</h4>
                <p className="text-muted-foreground whitespace-pre-wrap">{selectedExpert?.bio}</p>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-foreground mb-2">Commission Override (Optional)</h4>
                <div className="max-w-md">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={commissionOverride}
                    onChange={(e) => setCommissionOverride(e?.target?.value)}
                    placeholder="Leave empty for default 10%"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Set a custom commission percentage for this expert (0-100%). Leave empty to use
                    platform default (10%).
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="success"
                  iconName="CheckCircle"
                  onClick={() => handleApprove(selectedExpert?.id)}
                >
                  Approve Expert
                </Button>
                <Button
                  variant="error"
                  iconName="XCircle"
                  onClick={() => handleReject(selectedExpert?.id)}
                >
                  Reject Application
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpertApprovalQueue;